/**
 * Truy cập dữ liệu phân hệ Quản lý module (plugin platform, `module-registry`
 * qua Gateway, nhóm route nền tảng — ADR-0007).
 *
 * ⚠️ **Nối thẳng API thật**, trước khi có chủ sở hữu `code` theo đối tác và
 * khoá ký theo đối tác (`G3-D1-7`, `G3-D1-8` — chưa có ADR ghi nhận, mới là
 * đề xuất). Chấp nhận được lúc này vì chỉ FoxAI nộp gói; ai cũng ký được gói
 * mạo danh `code` của người khác cho tới khi hai mã đó xong — không mở cho
 * đối tác ngoài trước đó.
 *
 * `GET /api/v1/platform/modules` trả **phẳng** `ModuleVersion[]`, không nhóm
 * theo module — `gomTheoCode` ở `nghiep-vu/thao-tac.ts` dựng danh sách module
 * phía client.
 */
import { dungApiThat, goiApi } from '../api/http';
import { mockRequest } from '../../mock/transport';
import {
  danhSachPhienBanGia,
  duyetGia,
  xoaModuleGia,
  nopGoiGia,
  tuChoiGia,
} from '../../mock/quan-ly-module';
import type {
  ModulePackageResult,
  ModuleVersion,
  ModuleVersionPage,
} from '../../modules/quan-ly-module/types';

const GOC_PLATFORM = '/api/v1/platform/modules';

// `Limit` dùng chung của mọi endpoint phân trang (openapi/platform-api.yaml,
// components/parameters/Limit): maximum 100 — gửi quá bị từ chối 400
// INVALID_ARGUMENT, không phải một con số tự chọn.
const LIMIT_TOI_DA = 100;

/** Lấy hết trang qua `next_cursor`, không dừng ở trang đầu — tránh cắt mất âm thầm khi vượt một trang. */
async function layHetTrang(): Promise<ModuleVersion[]> {
  const tatCa: ModuleVersion[] = [];
  let cursor: string | undefined;
  for (;;) {
    const trang = await goiApi<ModuleVersionPage>(GOC_PLATFORM, {
      query: { limit: LIMIT_TOI_DA, cursor },
    });
    tatCa.push(...trang.items);
    if (!trang.next_cursor) return tatCa;
    cursor = trang.next_cursor;
  }
}

export const quanLyModuleRepository = {
  /**
   * `GET /api/v1/platform/modules` — mọi phiên bản đã nộp, mọi trạng thái,
   * mới nộp trước (ADR-0046 mục 2). Lấy hết trang qua `next_cursor`
   * (`layHetTrang`) — quy mô hàng chờ duyệt ở MVP nhỏ, chấp nhận gọi vài lần.
   */
  danhSachPhienBan: (): Promise<ModuleVersion[]> =>
    dungApiThat() ? layHetTrang() : mockRequest(danhSachPhienBanGia(), []),

  /**
   * `POST /api/v1/vendor/modules` (ADR-0045) — nộp gói zip, multipart. Server
   * đọc `module.yaml` trong gói để lấy `code`, `name`, `version`… — form
   * không có ô nhập lại những trường đó.
   */
  nopGoi: (tep: File, khoaLap: string): Promise<ModulePackageResult> =>
    dungApiThat()
      ? goiApi<ModulePackageResult>('/api/v1/vendor/modules', {
          method: 'POST',
          idempotencyKey: khoaLap,
          bieuMau: (() => {
            const fd = new FormData();
            fd.append('file', tep);
            return fd;
          })(),
        })
      : mockRequest(undefined).then(() => nopGoiGia(tep)),

  /**
   * `POST /api/v1/platform/modules/{code}/versions/{version}/approve` (ADR-0046).
   *
   * `body: {}` chứ không `undefined` khi không có ghi chú: generated `ModulesApi` đòi
   * `Content-Type` hợp lệ dù `ModuleReviewNote` optional trong OpenAPI — thiếu
   * header thì Spring trả `415` trước khi vào tới nghiệp vụ.
   */
  duyet: (code: string, version: string, note?: string): Promise<ModuleVersion> =>
    dungApiThat()
      ? goiApi<ModuleVersion>(
          `${GOC_PLATFORM}/${encodeURIComponent(code)}/versions/${encodeURIComponent(version)}/approve`,
          { method: 'POST', body: note ? { note } : {} },
        )
      : mockRequest(undefined).then(() => duyetGia(code, version)),

  /** `POST /api/v1/platform/modules/{code}/versions/{version}/reject` (ADR-0046). */
  tuChoi: (code: string, version: string, reason: string): Promise<ModuleVersion> =>
    dungApiThat()
      ? goiApi<ModuleVersion>(
          `${GOC_PLATFORM}/${encodeURIComponent(code)}/versions/${encodeURIComponent(version)}/reject`,
          { method: 'POST', body: { reason } },
        )
      : mockRequest(undefined).then(() => tuChoiGia(code, version, reason)),

  /**
   * `DELETE /api/v1/platform/modules/{code}` — xoá module do người dùng nộp cùng mọi phiên bản. 409
   * `MODULE_STATUS_CONFLICT` khi còn tenant cài, còn database module hoặc là module hệ thống.
   */
  xoa: (code: string): Promise<void> =>
    dungApiThat()
      ? goiApi<void>(`${GOC_PLATFORM}/${encodeURIComponent(code)}`, { method: 'DELETE' })
      : mockRequest(undefined).then(() => xoaModuleGia(code)),
};
